import { makeStyles } from "tss-react/mui";
import { MiIcon, MiIconButton } from "@miplatform/components";
import { mdiClose, mdiPlus } from "@mdi/js";
import { Box, Typography } from "@mui/material";
import { CATEGORY_STATUS } from "../../Configuration/AreaTypes/AreaTypes.types";

/**
 * @param {Object} props
 * @param {Object} [props.onDismiss]
 * @param {boolean} [props.disabled]
 * @param {boolean} [props.isSelected]
 * @param {boolean} [props.preview]
 * @param {string} [props.status]
 * @param {Object} props.category
 * @param {function} [props.onClick]
 */

export function PlaceholderMaterial({
  category,
  isSelected,
  disabled = false,
  preview = false,
  status,
  onClick = () => {},
  onDismiss = null,
}) {
  const { classes, theme } = useStyles({ isSelected, disabled, status });
  const isRequired = category.isRequired || false;

  const handleDismiss = (e) => {
    e.stopPropagation();
    onDismiss();
  };

  const handleOnClick = () => {
    if (!disabled && !preview) {
      onClick();
    }
  };

  const getFontColor = () => {
    if (disabled) {
      return theme.palette.grey.accent;
    }
    if (isRequired) {
      return theme.palette.primary.main;
    }
    return theme.palette.grey.darkerAccent;
  };

  const cardClasses = `${classes.card} ${
    disabled ? classes.cardDisabled : classes.cardEnabled
  } ${preview ? classes.cardPreview : ""}`;

  return (
    <Box className={cardClasses} onClick={handleOnClick}>
      <Box display="flex" gap={theme.spacing(1)} alignItems="center">
        <MiIcon
          path={mdiPlus}
          color={getFontColor()}
          size={0.8}
          sx={{ transition: "color 0.2s ease" }}
        />
        <Typography
          fontWeight={theme.fontWeight.semiBold}
          color={getFontColor()}
          variant="subtitle2"
          sx={{ transition: "color 0.2s ease" }}
        >
          Add {category.categoryName}
          {isRequired ? " (Required)" : " (Optional)"}
        </Typography>
      </Box>
      {!isRequired && onDismiss ? (
        <MiIconButton
          onClick={(e) => handleDismiss(e)}
          disableFocusRipple={false}
          disabled={disabled || preview}
        >
          <MiIcon path={mdiClose} size={0.8} />
        </MiIconButton>
      ) : (
        status && (
          <Typography
            variant="caption"
            fontWeight={theme.fontWeight.semiBold}
            color={
              status === CATEGORY_STATUS.ADDED
                ? theme.palette.success.main
                : theme.palette.error.main
            }
          >
            {status}
          </Typography>
        )
      )}
    </Box>
  );
}
const useStyles = makeStyles()((theme, { isSelected, status }) => {
  const getBackgroundColor = () => {
    if (status === CATEGORY_STATUS.ADDED) return theme.palette.success.light;
    if (status === CATEGORY_STATUS.REMOVED) return theme.palette.error.light;
    if (isSelected) return theme.palette.primary.light;
    return theme.palette.common.white;
  };

  return {
    card: {
      height: 32,
      marginTop: theme.spacing(2),
      width: "100%",
      border: `1px dashed ${theme.palette.grey.accent}`,
      borderRadius: 4,
      display: "flex",
      padding: theme.spacing(0, 2),
      alignItems: "center",
      justifyContent: "space-between",
      transition: "background-color 0.2s ease",
      opacity: status === CATEGORY_STATUS.REMOVED ? 0.7 : 1,
      "& .MuiTypography-root": {
        lineHeight: 1,
      },
    },
    cardEnabled: {
      backgroundColor: getBackgroundColor(),
      "&:hover": {
        cursor: "pointer",
        backgroundColor: isSelected
          ? theme.palette.primary.light
          : theme.palette.grey.light,
      },
    },
    cardDisabled: {
      backgroundColor: getBackgroundColor(),
      "&:hover": {
        cursor: "default",
      },
    },
    cardPreview: {
      backgroundColor: getBackgroundColor(),
      "&:hover": {
        cursor: "default",
      },
    },
  };
});
